import { formatDate, formatDateTime, formatMoney } from '@persona/shared';
import { Alert, Button, Card, Input, Skeleton, StatusPill } from '@persona/ui';
import { useEffect, useState } from 'react';
import { adminApi, ApiError, type AdminClientRow, type AdminSummary } from '../api';
import { EmptyRow, Th } from '../components/Shell';

const PAGE_SIZE = 50;

/** Названия согласий для интерфейса: коды из базы сотруднику ничего не говорят. */
const CONSENT_LABELS: Record<string, string> = {
  personal_data: 'Персональные данные',
  offer: 'Оферта',
  recurrent: 'Списания',
  marketing: 'Рассылка',
};

/** Клиенты, зарегистрировавшиеся через личный кабинет. */
export function ClientsPage({ onOpenClient }: { onOpenClient: (id: string) => void }) {
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [clients, setClients] = useState<AdminClientRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .summary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  useEffect(() => {
    // Поиск не дёргает сервер на каждой букве.
    const timer = window.setTimeout(() => {
      setClients(null);
      adminApi
        .clients(search, PAGE_SIZE, page * PAGE_SIZE)
        .then((result) => {
          setClients(result.items);
          setTotal(result.total);
        })
        .catch((cause: unknown) => {
          setError(cause instanceof ApiError ? cause.message : 'Не удалось загрузить список');
          setClients([]);
        });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [search, page]);

  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold tracking-wide uppercase">Клиенты кабинета</h1>
      <p className="mt-2 text-sm text-secondary">
        Здесь только те, кто зарегистрировался через личный кабинет. Клиенты, оформленные на ресепшене, живут в 1С —
        их покупки видны в карточке клиента.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryTile label="Всего клиентов" value={summary?.clients} />
        <SummaryTile label="Активных подписок" value={summary?.activeSubscriptions} />
        <SummaryTile label="Заканчиваются за неделю" value={summary?.expiringIn7Days} />
        <SummaryTile
          label="Оплачено за 30 дней"
          value={summary?.paidLast30Days}
          hint={summary ? formatMoney(summary.revenueLast30DaysMinor) : undefined}
        />
      </div>

      {/* Тихий сбой: деньги списаны, а продажи в 1С нет. Такое надо чинить сразу. */}
      {summary && summary.notPostedToOnec > 0 && (
        <Alert tone="danger" className="mt-4" title="Платежи не зафиксированы в 1С">
          {summary.notPostedToOnec} успешных платежей не доехали до 1С. Продажи в учёте клуба отсутствуют.
        </Alert>
      )}

      {/* Напоминание убрать следы показа: в отчётность эти записи попадать не должны */}
      {summary && summary.demoRecords > 0 && (
        <Alert tone="warning" className="mt-4" title="В базе есть демонстрационные записи">
          Демонстрационных платежей: {summary.demoRecords}. Денег по ним не было, в 1С они не передавались и в выручку
          не входят. После показа удалите их командой{' '}
          <code className="font-mono text-xs">npm run db:clear-demo --workspace @persona/api -- --yes</code>
        </Alert>
      )}

      {error && <Alert tone="danger" className="mt-6">{error}</Alert>}

      <div className="mt-8 max-w-md">
        <Input
          type="search"
          placeholder="Поиск по телефону, имени, почте или ID в 1С"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
        />
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-strong)]">
              <Th>Клиент</Th>
              <Th>Телефон</Th>
              <Th>Почта</Th>
              <Th>Абонемент</Th>
              <Th>Согласия</Th>
              <Th>Регистрация</Th>
              <Th>Последний вход</Th>
            </tr>
          </thead>
          <tbody>
            {clients === null ? (
              Array.from({ length: 5 }, (_, index) => (
                <tr key={index} className="border-b border-[var(--border-subtle)]">
                  <td className="px-3 py-4" colSpan={7}>
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))
            ) : clients.length === 0 ? (
              <EmptyRow colSpan={7}>
                {search ? 'Никого не нашли по этому запросу' : 'Через кабинет ещё никто не зарегистрировался'}
              </EmptyRow>
            ) : (
              clients.map((client) => (
                <tr key={client.id} className="border-b border-[var(--border-subtle)] align-top">
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => onOpenClient(client.id)}
                      className="text-left font-medium text-[var(--accent)] hover:underline"
                    >
                      {client.fullName ?? 'Не заполнено'}
                    </button>
                    {client.onecClientId && (
                      <div className="mt-0.5 font-mono text-xs text-muted">1С: {client.onecClientId.slice(0, 8)}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">{client.phone}</td>
                  <td className="px-3 py-3">{client.email ?? <span className="text-muted">—</span>}</td>
                  <td className="px-3 py-3">
                    {client.subscription ? (
                      <div className="flex flex-col gap-1">
                        <span>{client.subscription.tariffTitle}</span>
                        {client.subscription.currentPeriodEnd && (
                          <span className="text-xs text-muted">
                            до {formatDate(client.subscription.currentPeriodEnd)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <StatusPill>Нет</StatusPill>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {client.consents.map((consent) => (
                        <span key={consent} className="rounded bg-[var(--surface-muted)] px-2 py-0.5 text-xs">
                          {CONSENT_LABELS[consent] ?? consent}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-muted">{formatDate(client.registeredAt)}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-muted">
                    {client.lastLoginAt ? formatDateTime(client.lastLoginAt) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="mt-6 flex items-center gap-4">
          <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Назад
          </Button>
          <span className="text-sm text-muted">
            Страница {page + 1} из {pages} · всего {total}
          </span>
          <Button variant="secondary" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
            Дальше
          </Button>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, hint }: { label: string; value: number | undefined; hint?: string }) {
  return (
    <Card>
      <p className="text-xs tracking-wide text-muted uppercase">{label}</p>
      {value === undefined ? (
        <Skeleton className="mt-2 h-8 w-16" />
      ) : (
        <>
          <p className="font-display mt-2 text-3xl font-semibold">{value}</p>
          {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
        </>
      )}
    </Card>
  );
}
